/* src/pages/admin/manager/documentos/HistoricoDocumentosPage.tsx
   —————————————————————————————————————————————————————
   Vista de histórico de envíos / firmas
   · Filtros por usuario, tipo, estado y texto libre
   · Vista previa y descarga del PDF firmado (vía XHR con auth)
   · Re-envío del e-mail de firma
   · Soporta justificantes (AdjuntosController)
   · Feedback visual: spinner por fila al ver/descargar
   ————————————————————————————————————————————————————— */

import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  CircularProgress,
} from "@mui/material";
import { DataGrid, esES } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/MarkEmailUnread";
import dayjs from "dayjs";

import api from "../../../../api/axios";
import { useDocumentTypes } from "../../../../hooks/documentos/useDocumentTypes";
import { useUsuarios } from "../../../../hooks/useUsuarios";
import { useAuth } from "../../../../context/AuthContext";

/* ─────────────── tipos ─────────────── */
type Estado = "SENT" | "SIGNED" | "DECLINED" | "ERROR";

interface Row {
  id: number; // docId  o  idAdj (para justificantes)
  name: string;
  tipo: string; // “Justificante” o resto
  estado: Estado;
  sentAt: string | null;
  signedAt?: string | null;
  comentario?: string;
  interno?: string;
}

/* ─────────────── helpers UI ─────────────── */
const ESTADO_COLOR: Record<Estado, "warning" | "success" | "error"> = {
  SENT: "warning",
  SIGNED: "success",
  DECLINED: "error",
  ERROR: "error",
};
const EstadoChip = ({ value }: { value: Estado }) => (
  <Chip size="small" label={value} color={ESTADO_COLOR[value]} />
);

/* ========= selector de usuarios ========= */
function UserAutocomplete({ value, onChange }: { value?: number; onChange: (v?: number) => void }) {
  const { rows, loading, refresh } = useUsuarios();
  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <Autocomplete
      size="small"
      options={rows}
      loading={loading}
      value={rows.find((u) => u.id === value) ?? null}
      onChange={(_, v) => onChange(v?.id)}
      sx={{ minWidth: 260 }}
      getOptionLabel={(u) => u?.nombreCompleto ?? u?.email ?? ""}
      renderInput={(p) => <TextField {...p} label="Usuario" placeholder="Todos" />}
      isOptionEqualToValue={(o, v) => o.id === v.id}
    />
  );
}

/* ========= selector de tipos de documento ========= */
function DocTypeSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { data = [], isLoading } = useDocumentTypes();
  return (
    <TextField
      select
      size="small"
      label="Tipo doc."
      sx={{ minWidth: 200 }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={isLoading}
    >
      <MenuItem value="">Todos</MenuItem>
      {data.map((t) => (
        <MenuItem key={t.docTypeId} value={String(t.docTypeId)}>
          {t.nombre}
        </MenuItem>
      ))}
    </TextField>
  );
}

/* ═════════════ componente principal ═════════════ */
export default function HistoricoDocumentosPage() {
  /* filtros */
  const { user } = useAuth();
  const [userId, setUser] = useState<number>();
  const [docType, setType] = useState("");
  const [estado, setEst] = useState<Estado | "">("");
  const [q, setQ] = useState("");

  /* tabla */
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoad] = useState(false);

  /* loader por fila: 'preview' | 'download' */
  const [busy, setBusy] = useState<Record<number, "preview" | "download">>({});
  const setRowBusy = (id: number, kind: "preview" | "download") =>
    setBusy((prev) => ({ ...prev, [id]: kind }));
  const clearRowBusy = (id: number) =>
    setBusy((prev) => {
      const c = { ...prev };
      delete c[id];
      return c;
    });

  /* saber si puedo ver comentarioInterno */
  const canSeeInterno =
    user?.roles?.some((r) => ["admin", "hr", "facturacion"].includes(r)) ?? false;

  /* ------------ carga ------------ */
  useEffect(() => {
    if (!userId) {
      setRows([]);
      return;
    }
    setLoad(true);

    api
      .get("/documentos/historico", {
        params: {
          userId,
          docTypeId: docType || undefined,
          estado: estado || undefined,
          q: q || undefined,
        },
      })
      .then((res) => {
        const list = Array.isArray(res.data?.entities) ? res.data.entities : [];
        setRows(
          list.map((d: any) => ({
            id: d.docId,
            name: d.originalName,
            tipo: d.docType?.nombre, // ‘Justificante’ o cualquier otro
            estado: d.dd_estado as Estado,
            sentAt: d.sr_sent_at ?? null,
            signedAt: d.dd_signed_at ?? null,
            comentario: d.comentario ?? "",
            interno: d.comentarioInterno ?? "",
          }))
        );
      })
      .finally(() => setLoad(false));
  }, [userId, docType, estado, q]);

  /* ------------ helpers descarga/preview (XHR con token) ------------ */
  async function fetchDocumentoBlob(id: number) {
    const res = await api.get(`/documentos/${id}/pdf`, {
      params: { stream: 1 }, // fuerza stream → no redirige a Drive
      responseType: "blob",
    });
    return res.data as Blob;
  }

  async function fetchAdjuntoBlob(id: number) {
    const res = await api.get(`/adjuntos/${id}`, { responseType: "blob" });
    return res.data as Blob;
  }

  const isJustificante = (row: Row) => row.tipo === "Justificante";

  const previewFile = async (row: Row) => {
    setRowBusy(row.id, "preview");
    try {
      const blob = isJustificante(row)
        ? await fetchAdjuntoBlob(row.id)
        : await fetchDocumentoBlob(row.id);

      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      // opcional: toast/snackbar de error
    } finally {
      clearRowBusy(row.id);
    }
  };

  const downloadFile = async (row: Row) => {
    setRowBusy(row.id, "download");
    try {
      const blob = isJustificante(row)
        ? await fetchAdjuntoBlob(row.id)
        : await fetchDocumentoBlob(row.id);

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (row.name ?? `documento_${row.id}`).replace(/\.pdf$/i, "") + ".pdf";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      // opcional: toast/snackbar de error
    } finally {
      clearRowBusy(row.id);
    }
  };

  const resend = (id: number) =>
    api.post(`/documentos/${id}/resend-email`).then(() => alert("Reenviado 👍"));

  /* ------------ columnas ------------ */
  const cols = useMemo(() => {
    const base = [
      { field: "name", headerName: "Documento", flex: 1 },
      { field: "tipo", headerName: "Tipo", width: 160 },
      {
        field: "estado",
        headerName: "Estado",
        width: 120,
        renderCell: ({ value }: any) => <EstadoChip value={value} />,
      },
      {
        field: "sentAt",
        headerName: "Enviado",
        width: 170,
        valueGetter: ({ value }: any) => (value ? dayjs(value).format("DD/MM/YYYY HH:mm") : ""),
      },
      {
        field: "signedAt",
        headerName: "Firmado",
        width: 170,
        valueGetter: ({ value }: any) => (value ? dayjs(value).format("DD/MM/YYYY HH:mm") : ""),
      },
      {
        field: "comentario",
        headerName: "Comentario",
        flex: 1,
        renderCell: ({ value }: any) => value || "—",
      },
    ] as any[];

    if (canSeeInterno)
      base.push({
        field: "interno",
        headerName: "Comentario interno",
        flex: 1,
        renderCell: ({ value }: any) => value || "—",
      });

    base.push({
      field: "actions",
      headerName: "",
      width: 160,
      sortable: false,
      renderCell: ({ row }: { row: Row }) => {
        const isBusy = !!busy[row.id];
        return (
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={() => previewFile(row)}
              disabled={row.estado !== "SIGNED" || isBusy}
            >
              {busy[row.id] === "preview" ? (
                <CircularProgress size={16} />
              ) : (
                <VisibilityIcon fontSize="inherit" />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => downloadFile(row)}
              disabled={row.estado !== "SIGNED" || isBusy}
            >
              {busy[row.id] === "download" ? (
                <CircularProgress size={16} />
              ) : (
                <DownloadIcon fontSize="inherit" />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => resend(row.id)}
              disabled={row.estado === "SIGNED" || isBusy}
            >
              <EmailIcon fontSize="inherit" />
            </IconButton>
          </Stack>
        );
      },
    });

    return base;
  }, [canSeeInterno, busy]);

  /* ------------ UI ------------ */
  return (
    <Box>
      {/* filtros */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
        <UserAutocomplete value={userId} onChange={setUser} />
        <DocTypeSelect value={docType} onChange={setType} />
        <TextField
          select
          size="small"
          label="Estado"
          value={estado}
          onChange={(e) => setEst(e.target.value as Estado | "")}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {["SENT", "SIGNED", "DECLINED", "ERROR"].map((st) => (
            <MenuItem key={st} value={st}>
              {st}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Buscar…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <Button onClick={() => setQ("")} disabled={!q}>
          Limpiar
        </Button>
      </Stack>

      <DataGrid
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        rows={rows}
        columns={cols}
        loading={loading}
        rowsPerPageOptions={[25, 50, 100]}
        pageSize={50}
        disableSelectionOnClick
      />
    </Box>
  );
}
